import { useState } from "react";
import { FileText, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { field } from "@/components/admin/SettingsPanel";
import { UploadBtn } from "@/components/admin/SermonForm";

const EMPTY = { name: "", role_en: "", role_mh: "", photo_url: "", bio_en: "", bio_mh: "", files: [] };

const L = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block font-ui text-[11px] uppercase tracking-widest text-[#B7BFCC]">{label}</span>
    {children}
  </label>
);

export const PastorForm = ({ initial, pastors = [], onClose, onSaved }) => {
  const [f, setF] = useState({ ...EMPTY, ...(initial || {}) });
  const [busy, setBusy] = useState(false);
  const norm = (s) => s.replace(/\s+/g, " ").trim().toLowerCase();
  const dup = f.name.trim() && pastors.find((p) => p.id !== initial?.id && norm(p.name) === norm(f.name));
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const setFile = (i, k) => (e) => setF((p) => ({ ...p, files: p.files.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)) }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k] ?? EMPTY[k]]));
      const { data } = initial?.id ? await api.put(`/pastors/${initial.id}`, body) : await api.post("/pastors", body);
      onSaved(!!data?.pending, !!data?.merged);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pastor-form-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto border-[#D4AF37]/20 bg-[#131C2E] text-[#F4EEE2]">
        <DialogHeader><DialogTitle className="font-display text-3xl">{initial?.id ? "Edit pastor" : "Add pastor"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2"><L label="Name (must match the “Pastor / Speaker” name on their messages)"><input data-testid="pastor-form-name" required value={f.name} onChange={set("name")} className={field} /></L>
            {dup && <p data-testid="pastor-form-dup-warning" className="mt-2 rounded-xl bg-[#D4AF37]/15 px-3 py-2 text-xs text-[#F4EEE2]">{initial?.id ? `${dup.name} already has a card. Please use a different name.` : `${dup.name} already has a card. When you save, the files will be ADDED to that card. No new card will be made.`}</p>}
          </div>
          <L label="Title / role (English)"><input data-testid="pastor-form-role-en" placeholder="Senior Pastor" value={f.role_en} onChange={set("role_en")} className={field} /></L>
          <L label="Title / role (Marshallese)"><input data-testid="pastor-form-role-mh" value={f.role_mh} onChange={set("role_mh")} className={field} /></L>
          <div className="sm:col-span-2">
            <L label="Photo">
              <div className="flex items-center gap-3">
                {f.photo_url && <img src={fileUrl(f.photo_url)} alt="" className="h-16 w-16 rounded-full object-cover" />}
                <UploadBtn accept="image/*" testid="pastor-form-photo-upload" label={f.photo_url ? "Replace photo" : "Upload photo"} onDone={(u) => setF((p) => ({ ...p, photo_url: u }))} />
              </div>
            </L>
          </div>
          <div className="sm:col-span-2"><L label="Short bio (English)"><textarea data-testid="pastor-form-bio-en" rows={4} value={f.bio_en} onChange={set("bio_en")} className={field} /></L></div>
          <div className="sm:col-span-2"><L label="Short bio (Marshallese)"><textarea data-testid="pastor-form-bio-mh" rows={4} value={f.bio_mh} onChange={set("bio_mh")} className={field} /></L></div>
          <div className="sm:col-span-2">
            <L label="Sermon files for this pastor only (PDF, Word, PowerPoint, audio, photos, any format)">
              <div className="grid gap-2">
                <p className="text-xs text-[#B7BFCC]">Give each file a title and date. On the website, the newest date shows first. No date? The upload day is used.</p>
                {f.files.map((a, i) => (
                  <div key={a.url} data-testid={`pastor-form-file-${i}`} className="flex flex-wrap items-center gap-2 rounded-xl border border-[#F4EEE2]/10 px-3 py-2">
                    <FileText className="h-4 w-4 shrink-0 text-[#D4AF37]" />
                    <input data-testid={`pastor-form-file-title-${i}`} value={a.title || ""} placeholder={a.name || "Title"} aria-label="File title" onChange={setFile(i, "title")} className="min-w-[140px] flex-1 rounded-lg border border-[#F4EEE2]/10 bg-transparent px-2 py-1.5 text-sm text-[#F4EEE2] outline-none" />
                    <input data-testid={`pastor-form-file-date-${i}`} type="date" value={a.date || ""} aria-label="File date" onChange={setFile(i, "date")} className="rounded-lg border border-[#F4EEE2]/10 bg-transparent px-2 py-1.5 text-sm text-[#F4EEE2] outline-none" />
                    <button type="button" data-testid={`pastor-form-file-remove-${i}`} onClick={() => setF((p) => ({ ...p, files: p.files.filter((_, j) => j !== i) }))} className="rounded-full p-1.5 text-[#F2A08A]" aria-label="Remove file"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                <div><UploadBtn multiple library testid="pastor-form-file-upload" label="Upload sermon files" onDone={(u, file) => setF((p) => ({ ...p, files: [...p.files, { url: u, name: file.name, type: file.type }] }))} /></div>
              </div>
            </L>
          </div>
          <button data-testid="pastor-form-save-btn" disabled={busy} className="flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] py-3 font-ui font-semibold text-[#0B111E] disabled:opacity-60 sm:col-span-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save pastor
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
